
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { utcToLocalDateTime } from "@/utils/timezoneUtils";

export interface ProviderBooking {
  id: string;
  provider_id: string;
  service_id: string;
  user_id: string;
  booking_number: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  booking_date: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  price: number;
  currency: string;
  status: 'confirmed' | 'cancelled' | 'completed' | 'no_show' | 'pending';
  notes?: string;
  time_slot_id?: string;
  checked_in_at?: string;
  checked_out_at?: string;
  created_at: string;
  updated_at: string;
  service?: {
    name: string;
    service_type: string;
  };
  provider?: {
    name: string;
  };
  service_name?: string;
  provider_name?: string;
}

export const useProviderBookings = (providerId?: string) => {
  return useQuery({
    queryKey: ['provider-bookings', providerId],
    queryFn: async () => {
      console.log('Fetching provider bookings for providerId:', providerId);
      
      if (!providerId) {
        console.log('No providerId provided, returning empty array');
        return [];
      }
      
      // First, let's check if the current user can access this provider
      const { data: user } = await supabase.auth.getUser();
      console.log('Current user:', user.user?.email);
      
      // Check if user is admin
      const { data: adminCheck } = await supabase
        .from('admin_users')
        .select('id')
        .eq('user_id', user.user?.id)
        .maybeSingle();
      
      console.log('Admin check result:', adminCheck);
      
      // Check provider assignment
      const { data: assignmentCheck } = await supabase
        .from('user_provider_assignments')
        .select('id')
        .eq('user_id', user.user?.id)
        .eq('provider_id', providerId)
        .maybeSingle();
      
      console.log('Provider assignment check result:', assignmentCheck);
      
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          *,
          service:services(name, service_type),
          provider:providers(name)
        `)
        .eq('provider_id', providerId)
        .order('booking_date', { ascending: true })
        .order('start_time', { ascending: true });

      if (error) {
        console.error('Error fetching provider bookings:', error);
        throw error;
      }

      console.log('Provider bookings fetched successfully:', data?.length || 0, 'bookings');
      console.log('Sample booking data:', data?.[0]);
      
      // Transform the data to include service_name and provider_name for compatibility
      const transformedData = (data || []).map(booking => {
        // FIXED: Use consistent timezone utility for proper UTC to local conversion
        // Times are stored as UTC times in HH:MM:SS format, convert to local display
        const bookingStartUTC = `${booking.booking_date}T${booking.start_time}Z`;
        const bookingEndUTC = `${booking.booking_date}T${booking.end_time}Z`;
        
        // Convert using timezone utilities for consistency
        const startTimeLocal = utcToLocalDateTime(bookingStartUTC);
        const endTimeLocal = utcToLocalDateTime(bookingEndUTC);
        
        console.log(`Provider booking ${booking.id} time conversion:`, {
          storedStart: booking.start_time,
          storedEnd: booking.end_time,
          utcStart: bookingStartUTC,
          utcEnd: bookingEndUTC,
          localStart: startTimeLocal.localTimeDisplay,
          localEnd: endTimeLocal.localTimeDisplay
        });

        return {
          ...booking,
          start_time: startTimeLocal.localTimeDisplay,
          end_time: endTimeLocal.localTimeDisplay,
          service_name: booking.service?.name,
          provider_name: booking.provider?.name,
        };
      }) as ProviderBooking[];
      
      return transformedData;
    },
    enabled: !!providerId,
  });
};

export const useProviderBookingStats = (providerId?: string) => {
  return useQuery({
    queryKey: ['provider-booking-stats', providerId],
    queryFn: async () => {
      console.log('Fetching provider booking stats for providerId:', providerId);
      
      if (!providerId) {
        console.log('No providerId provided for stats, returning null');
        return null;
      }
      
      // Fetch booking data
      const { data: bookingsData, error: bookingsError } = await supabase
        .from('bookings')
        .select('status, price')
        .eq('provider_id', providerId);

      if (bookingsError) {
        console.error('Error fetching booking stats:', bookingsError);
        throw bookingsError;
      }

      console.log('Booking stats data:', bookingsData?.length || 0, 'records');

      // Fetch membership purchases data - include both 'paid' and 'completed' status
      const { data: membershipData, error: membershipError } = await supabase
        .from('membership_purchases')
        .select('status, amount, coupon_discount_amount')
        .eq('provider_id', providerId)
        .in('status', ['paid', 'completed']); // Include both paid and completed purchases

      if (membershipError) {
        console.error('Error fetching membership purchase stats:', membershipError);
        throw membershipError;
      }

      console.log('Membership purchase stats data:', membershipData?.length || 0, 'records');

      // Calculate booking revenue
      const bookingRevenue = bookingsData.reduce((sum, booking) => sum + (booking.price || 0), 0);
      
      // Calculate membership revenue (amount minus any coupon discounts)
      const membershipRevenue = membershipData.reduce((sum, purchase) => {
        const finalAmount = purchase.amount - (purchase.coupon_discount_amount || 0);
        return sum + finalAmount;
      }, 0);

      // Calculate total revenue including both bookings and memberships
      const totalRevenue = bookingRevenue + membershipRevenue;

      const stats = {
        totalBookings: bookingsData.length,
        confirmedBookings: bookingsData.filter(b => b.status === 'confirmed').length,
        completedBookings: bookingsData.filter(b => b.status === 'completed').length,
        totalRevenue: totalRevenue,
        cancelledBookings: bookingsData.filter(b => b.status === 'cancelled').length,
      };

      console.log('Calculated stats:', stats);
      console.log('Revenue breakdown:', {
        bookingRevenue,
        membershipRevenue,
        totalRevenue
      });
      
      return stats;
    },
    enabled: !!providerId,
  });
};
